import {
    Component,
    OnChanges,
    OnDestroy,
    EventEmitter,
    SimpleChanges,
    Input,
    Output,
} from '@angular/core';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';
import { RtClm, RtvTripError, RtTrip } from '@bds/railtrac-models';
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import {
    BdsOriginCriteriaDialogComponent,
    BdsOriginCriteriaDialogModel,
    RailtracClmService,
    RailtracTripService,
    RtTripCriteriaComponent,
    RtTripCriteriaDialogComponent,
    RtTripCriteriaDialogData,
} from '@bds/railtrac';
import {
    BdsDestinationCriteriaDialogComponent,
    BdsDestinationCriteriaDialogModel,
} from '@bds/destination-criteria';
import { ReferenceApplicationPathProvider } from '@bds/reference-pathing';

export type RtTripMoreOptionsButtonEvents =
    | 'addOriginCriteria'
    | 'addDestinationCriteria'
    | 'reapplyClms'
    | 'previousTrip';

@Component({
    selector: 'rt-trip-error-more-options',
    templateUrl: './rt-trip-error-more-options.component.html',
    styleUrls: ['./rt-trip-error-more-options.component.scss'],
})
export class RtTripErrorMoreOptionsComponent implements OnChanges, OnDestroy {
    @Input() trip: RtvTripError;
    @Input() clms: RtClm[] = [];
    @Input() isMultiTripError = false;
    @Output() change: EventEmitter<void> = new EventEmitter<void>();
    @Output() resolve: EventEmitter<RtTripMoreOptionsButtonEvents> =
        new EventEmitter<RtTripMoreOptionsButtonEvents>();

    iconMore = faEllipsisV;

    // Add destroy subject to prevent memory leaks
    private destroy$ = new Subject<void>();

    constructor(
        public dialog: MatDialog,
        public snackbar: MatSnackBar,
        private tripService: RailtracTripService,
        private clmService: RailtracClmService,
        private router: Router,
        private referenceRouting: ReferenceApplicationPathProvider,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (!!changes['trip']) {
            // Trip changed
        }
        if (!!changes['clms']) {
            // CLMs changed
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Open the Customer Alias page in a new tab.
     * @returns void
     */
    async customerAlias(): Promise<void> {
        try {
            const customerPath = await firstValueFrom(this.referenceRouting.Customers());
            console.log(customerPath);

            if (customerPath) {
                const baseCustomerPath = customerPath.replace(/\/customers?.*$/, '');
                const customerAliasPath = `${baseCustomerPath}/customeralias`;

                window.open(customerAliasPath, '_blank');
            } else {
                this.snackbar.open('Customer path not available', 'Error', { duration: 3000 });
            }
        } catch (error) {
            console.error('Error opening customer alias:', error);
            this.snackbar.open('Error opening customer alias', 'Error', { duration: 3000 });
        }
    }

    /**
     * Open the City Alias page in a new tab.
     * @returns void
     */
    async cityAlias(): Promise<void> {
        try {
            const customerPath = await firstValueFrom(this.referenceRouting.Customers());
            console.log(customerPath);

            if (customerPath) {
                const baseCustomerPath = customerPath.replace(/\/customers?.*$/, '');
                const customerAliasPath = `${baseCustomerPath}/citynames`;

                window.open(customerAliasPath, '_blank');
            } else {
                this.snackbar.open('Customer path not available', 'Error', { duration: 3000 });
            }
        } catch (error) {
            console.error('Error opening customer alias:', error);
            this.snackbar.open('Error opening customer alias', 'Error', { duration: 3000 });
        }
    }

    /**
     * Open the Previous Trip page in a new tab.
     * @returns void
     */
    async previousTrip(): Promise<void> {
        try {
            if (!this.trip?.carInit || !this.trip?.carNo) {
                this.snackbar.open('Missing car information', 'Error', { duration: 3000 });
                return;
            }

            // Get the current trip for the specific car
            const currentTrip = await firstValueFrom(
                this.tripService.findCurrentTrip(this.trip.carInit, this.trip.carNo),
            );

            if (!currentTrip) {
                this.snackbar.open('No current trip found for this car', 'Info', {
                    duration: 3000,
                });
                return;
            }

            this.snackbar.open(
                `Current trip found: ${currentTrip.carInit} ${currentTrip.carNo} - ${new Date(
                    currentTrip.shipDatetime,
                ).toLocaleDateString()}`,
                'Ok',
                { duration: 3000 },
            );

            const url = this.router.serializeUrl(
                this.router.createUrlTree(['/trip', currentTrip.ormId]),
            );
            window.open(url, '_blank');
        } catch (error) {
            this.snackbar.open('Error fetching current trip', 'Error', { duration: 3000 });
        }
    }

    // Fix the navigateToOriginCriteria method - add missing showOriginCriteria
    navigateToOriginCriteria(): void {
        try {
            if (!this.trip) {
                this.snackbar.open('No trip information available', 'Error', { duration: 3000 });
                return;
            }

            // Get current CLM data if available
            let currentClm: RtClm | null = null;
            if (this.clms && this.clms.length > 0) {
                currentClm = this.clms.reduce((latest: RtClm | null, current: RtClm) => {
                    if (!latest) return current;

                    const latestDate = new Date(latest.clmDateTime);
                    const currentDate = new Date(current.clmDateTime);

                    return currentDate > latestDate ? current : latest;
                }, null);
            }

            // Prepare dialog data for origin criteria
            const dialogData: RtTripCriteriaDialogData = {
                title: 'Origin Criteria',
                customerNumber: this.trip.custNo,
                destinationCity: currentClm?.destinationCity || this.trip.destCity,
                destinationState: currentClm?.destinationState || this.trip.destState,
                originCity: currentClm?.locationCity || this.trip.origCity,
                originState: currentClm?.locationState || this.trip.origState,
                tripId: this.trip.ormId,
                returnCity: this.trip.returnCity,
                returnState: this.trip.returnState,
                showDestinationCriteria: false,
                showOriginCriteria: true, // ADD THIS LINE
            };

            // Open the trip criteria dialog
            this.dialog.open(RtTripCriteriaDialogComponent, {
                width: '700px',
                data: dialogData,
                disableClose: false,
                autoFocus: true,
            });
        } catch (error) {
            console.error('Error opening origin criteria dialog:', error);
            this.snackbar.open('Error opening origin criteria', 'Error', { duration: 3000 });
        }
    }

    // Fix the navigateToDestinationCriteria method - add proper result handling
    async navigateToDestinationCriteria(): Promise<void> {
        try {
            if (!this.trip) {
                this.snackbar.open('No trip information available', 'Error', { duration: 3000 });
                return;
            }

            // Get current CLM data if available
            let currentClm: RtClm | null = null;
            try {
                currentClm = await firstValueFrom(
                    this.clmService.getCurrentClm(this.trip.carInit, this.trip.carNo),
                );
            } catch (error) {
                console.warn('Could not fetch current CLM:', error);
            }

            // Prepare dialog data with CLM information if available
            const dialogData: RtTripCriteriaDialogData = {
                title: 'Destination Criteria',
                customerNumber: this.trip.custNo,
                destinationCity: currentClm?.destinationCity || this.trip.destCity,
                destinationState: currentClm?.destinationState || this.trip.destState,
                originCity: currentClm?.locationCity || this.trip.origCity,
                originState: currentClm?.locationState || this.trip.origState,
                tripId: this.trip.ormId,
                returnCity: this.trip.returnCity,
                returnState: this.trip.returnState,
                showDestinationCriteria: true,
                showOriginCriteria: false,
            };

            // Open the trip criteria dialog
            this.dialog.open(RtTripCriteriaDialogComponent, {
                width: '700px',
                data: dialogData,
                disableClose: false,
                autoFocus: true,
            });
        } catch (error) {
            console.error('Error opening destination criteria dialog:', error);
            this.snackbar.open('Error opening destination criteria', 'Error', { duration: 3000 });
        }
    }

    // Fix all other dialog subscriptions to use takeUntil
    async addOriginCriteria(): Promise<void> {
        if (this.trip) {
            const clm = await firstValueFrom(
                this.clmService.getCurrentClm(this.trip.carInit, this.trip.carNo),
            );

            if (clm) {
                const originInfo: BdsOriginCriteriaDialogModel = {
                    returnCity: clm?.locationCity,
                    returnState: clm?.locationState,
                    locationCity: clm?.locationCity,
                    locationState: clm?.locationState,
                    railroad: clm?.road,
                    sightCode: clm?.sightCode,
                    isClone: false,
                    isNew: true,
                };

                const dialogRef = this.dialog.open(BdsOriginCriteriaDialogComponent, {
                    width: '550px',
                    data: originInfo,
                });

                dialogRef
                    .afterClosed()
                    .pipe(takeUntil(this.destroy$))
                    .subscribe((result) => {
                        if (result?.event == 'Save') {
                            this.snackbar.open('Origin criteria added', 'Ok', { duration: 3000 });
                            this.resolve.emit('addOriginCriteria');
                            this.change.emit();
                        }
                    });
            }
        } else {
            const originInfo: BdsOriginCriteriaDialogModel = {
                isClone: false,
                isNew: true,
            };

            const dialogRef = this.dialog.open(BdsOriginCriteriaDialogComponent, {
                width: '550px',
                data: originInfo,
            });

            dialogRef
                .afterClosed()
                .pipe(takeUntil(this.destroy$))
                .subscribe((result) => {
                    if (result?.event == 'Save') {
                        this.snackbar.open('Origin criteria added', 'Ok', { duration: 3000 });
                        this.resolve.emit('addOriginCriteria');
                        this.change.emit();
                    }
                });
        }
    }

    /**
     * Open the Destination Criteria dialog for the trip's customer.
     * @returns void
     */
    addDestinationCriteria(): void {
        if (this.trip) {
            if (!this.trip.custNo) {
                this.snackbar.open('Customer number is missing', 'Ok', { duration: 3000 });
                return;
            }

            const destinationInfo: BdsDestinationCriteriaDialogModel = {
                customerNo: this.trip.custNo,
                isClone: false,
                isNew: true,
            };

            const dialogRef = this.dialog.open(BdsDestinationCriteriaDialogComponent, {
                width: '550px',
                data: destinationInfo,
            });

            dialogRef
                .afterClosed()
                .pipe(takeUntil(this.destroy$))
                .subscribe((result) => {
                    if (result?.event == 'Save') {
                        this.snackbar.open('Destination criteria added', 'Ok', { duration: 3000 });
                        this.resolve.emit('addDestinationCriteria');
                        this.change.emit();
                    }
                });
        }
    }

    async reapplyClms(): Promise<void> {
        try {
            if (!this.trip) {
                this.snackbar.open('No trip information available', 'Error', { duration: 3000 });
                return;
            }

            // Show loading message
            this.snackbar.open('Reapplying CLMs...', 'Info', { duration: 2000 });

            // Reapply CLMs for the trip
            const reapplyResult = await firstValueFrom(
                this.clmService.reapplyClms(this.trip.carInit, this.trip.carNo),
            );

            if (reapplyResult) {
                this.snackbar.open(
                    `Successfully reapplied ${this.clms.length} CLM(s) for trip ${this.trip.carInit} ${this.trip.carNo}`,
                    'Success',
                    { duration: 3000 },
                );
            } else {
                this.snackbar.open('Failed to reapply CLMs', 'Error', { duration: 3000 });
            }
        } catch (error) {
            this.snackbar.open('Error reapplying CLMs', 'Error', { duration: 3000 });
        }
    }
}
